import { Amount } from '@/components/atoms/Amount/Amount'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { Heading, Flex, Text, Grid, Box, Button, Input, SimpleGrid, Skeleton, Icon } from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { GET_AUCTION_BASKET_BY_ID, GET_AUCTION_BASKETS, useGetUserProfileByAddress } from '@/api/query'
import { AuctionBasketStatus, Get_Auction_Basket_By_IdQuery, Get_Auction_BasketsQuery } from '@/__generated__/graphql'
import { useAppToast } from '@/hooks/useAppToast'
import { NoResultsFallback } from '@/components/molecules/NoResultsFallback/NoResultsFallback'
import { getTrimmedValue, TrimVariant } from '@/helpers/getTrimmedValue'
import { useEffect, useRef, useState, MouseEvent } from 'react'
import { formatAmount, toBigNumberInWei } from '@/helpers/formatAmount'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { useSession } from '@/context/SessionProvider'
import { useMutation } from '@tanstack/react-query'
import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import Logo from '@/public/assets/icons/logo.svg'
import { AUCTION_BASKET_CONTRACT_ADDRESS, EXPLORERS, INJ_DENOM, NBZ_DENOM, NBZ_DENOM_BASE } from '@/constants/constants'
import { Eye } from '@/icons'
import { useTranslation } from 'next-i18next/pages'

const transformAuctionBaskets = (data: Get_Auction_BasketsQuery | Get_Auction_Basket_By_IdQuery | undefined) => {
  const auctions = data?.auctionBaskets?.nodes || []

  const activeAuction = !!(data as any)?.auctionBasket ? (data as any).auctionBasket : auctions[0] || null

  return {
    activeAuction: {
      ...activeAuction,
      auctionBasketBets: activeAuction?.auctionBasketBets.nodes,
    },
    pastAuctions: auctions,
    totalBurned: data?.statistic?.auctionBurnToken || '0',
    tokenPrices: data?.tokenPrices?.nodes,
  }
}

export const Basket = ({ id }: { id?: string }) => {
  const { t } = useTranslation()
  const { onError } = useAppToast()
  const { data, loading, refetch, error } = useQuery(id ? GET_AUCTION_BASKET_BY_ID : GET_AUCTION_BASKETS, {
    pollInterval: id ? 0 : 5_000,
    variables: id ? { id: id as never } : undefined,
    onError: () => {
      onError()
    },
  })

  const auctions = transformAuctionBaskets(data)

  return (
    <>
      <BasketTitle totalBurned={auctions.totalBurned} />
      <Grid
        mb={10}
        display={{ base: 'flex', xl: 'grid' }}
        templateColumns={{ base: '1', xl: '380px auto', '2xl': '430px auto' }}
        flexDir={{ base: 'column-reverse', xl: 'unset' }}
        gap={6}
      >
        {!loading && !error ? (
          <>
            <BasketForm
              roundId={auctions.activeAuction?.id}
              startDate={auctions.activeAuction?.created}
              currentBid={auctions.activeAuction?.bet}
              endDate={auctions.activeAuction?.end}
              isHistorical={!!id}
              status={auctions.activeAuction.status as AuctionBasketStatus}
              refetch={() => refetch()}
            />
            <BasketRound auction={auctions.activeAuction as any} prices={auctions.tokenPrices as any} />
          </>
        ) : (
          <>
            <Skeleton h={'341px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
            <Skeleton h={'341px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
          </>
        )}
      </Grid>

      <BasketBidsTitleNumber loading={loading} bidsNumber={auctions.activeAuction?.auctionBasketBets?.length} />
      <BasketRoundBids loading={loading} bids={auctions.activeAuction?.auctionBasketBets as any} />
      <Heading alignItems={'center'} display={'flex'} mb={6} as={'h3'} fontSize={'xl'} gap={2}>
        {t('basket.rounds-history')}
      </Heading>
      <BasketHistory id={id} loading={loading} rounds={auctions.pastAuctions as any} />
    </>
  )
}

const BasketBidsTitleNumber = ({ bidsNumber = 0, loading }: { bidsNumber?: number; loading: boolean }) => {
  const { t } = useTranslation()
  return (
    <Heading alignItems={'center'} display={'flex'} mb={6} as={'h3'} fontSize={'xl'} gap={2}>
      <Text as={'span'}>{t('common.bids')}</Text>
      {!loading ? (
        <Text color={'ninja.200'} as={'span'}>
          {bidsNumber}
        </Text>
      ) : (
        <Skeleton startColor="ninja.400" endColor="ninja.500" w={5} h={6} display={'inline-block'} rounded={'lg'} />
      )}
    </Heading>
  )
}

import BurningToken from '@/public/assets/images/common/burning-token.png'
import { Image } from '@/components/HOC/Image'
import Link from 'next/link'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { Pulsar } from '@/components/atoms/Pulsar/Pulsar'
import { GameRules } from 'types/types'
import { TokenMoney } from '@ninjablaze/sdk-ts'

import { useBalanceStore } from '@/context/UserBalance'

const BasketRound = ({ auction, prices }: { auction: Auction; prices: Array<Price> }) => {
  const { t } = useTranslation()
  const bet = new TokenMoney(auction.bet, NBZ_DENOM)
  return (
    <Box
      rounded={'2xl'}
      pos={'relative'}
      overflow={'hidden'}
      p={6}
      boxShadow={'0px 4px 24px 0px #9B54FF inset, 0px 4px 64px 0px #9B54FF inset;'}
    >
      <Flex
        justifyContent={'space-between'}
        alignItems={'start'}
        flexDir={{ base: 'column-reverse', md: 'row' }}
        gap={{ base: 4, md: 0 }}
      >
        <Flex
          alignItems={'center'}
          py={1.5}
          px={3}
          gap={2.5}
          border={'1px solid'}
          borderColor={'ninja.400'}
          rounded={'lg'}
          mb={4}
        >
          <Text fontSize={'sm'} fontWeight={600}>
            {auction.status === AuctionBasketStatus.Active ? t('basket.current-bid') : t('common.winner')}
          </Text>
          {!!auction.leader && <UserProfile address={auction.leader} />}
        </Flex>
        <Flex alignItems={'start'} gap={2} fontSize={'sm'} fontWeight={600}>
          <Box
            px={3}
            py={2}
            bg={auction.status === AuctionBasketStatus.Finished ? 'brand.yellow' : '#2EB47C'}
            rounded={'lg'}
          >
            <Text>{auction.status?.slice(0, 1) + auction.status?.slice(1).toLowerCase()}</Text>
          </Box>
          <Box px={3} py={2} bg={'ninja.500'} rounded={'lg'}>
            <Text>
              {t('common.round')}{' '}
              <Text as={'span'} color={'ninja.200'}>
                #{auction.id}
              </Text>
            </Text>
          </Box>
          <Box px={3} py={2} bg={'ninja.500'} rounded={'lg'}>
            <Text>
              {t('common.bids')}{' '}
              <Text as={'span'} color={'ninja.200'}>
                {auction.auctionBasketBets?.length || 0}
              </Text>
            </Text>
          </Box>
        </Flex>
      </Flex>
      <Flex mb={4} gap={2} alignItems={'center'}>
        <Icon boxSize={{ base: 7, md: 10, xl: 12 }} as={Logo} />
        <Text lineHeight={{ base: '28px', xl: '48px' }} fontSize={{ base: '28px', xl: '48px' }} fontWeight={600}>
          {bet.humanLocaleString}{' '}
          <Text as={'span'} color={'ninja.200'}>
            NBZ
          </Text>
        </Text>
      </Flex>

      <Text mb={4} color={'ninja.300'} fontWeight={600}>
        ~
        {Number(
          (bet.humanNumber * Number(prices.find((x) => x.symbol == 'nbz')?.value || 0)).toFixed(2)
        ).toLocaleString()}{' '}
        USD
      </Text>
      <BasketRoundContents
        basket={auction.basket}
        prices={prices}
        isHistorical={auction.status == AuctionBasketStatus.Finished}
      />
      <Image
        display={{ base: 'none', '2xl': 'block' }}
        pos={'absolute'}
        boxSize={'280px'}
        transform={'scaleX(-1)'}
        right={'60px'}
        bottom={'10px'}
        src={BurningToken}
        alt=""
      />
    </Box>
  )
}

type BasketAsset = {
  Amount: number
  Denom: string
}

type Price = {
  symbol: string
  value: string
}

const BasketRoundContents = ({
  basket,
  prices,
  isHistorical,
}: {
  basket: Array<BasketAsset>
  prices: Array<Price>
  isHistorical: boolean
}) => {
  const { t } = useTranslation()

  const assets = basket.map((asset) => {
    const amount = formatAmount(asset.Amount.toString())
    const valuePerOne = parseFloat(prices?.find((price) => price.symbol === asset.Denom)?.value || '0')

    return {
      denom: asset.Denom,
      amount: amount,
      value: parseFloat(amount) * valuePerOne,
    }
  })

  const nbzPrice = Number(prices.find((price) => price.symbol === 'nbz')?.value || 0)
  const totalValue = assets.reduce((prev, curr) => prev + curr.value, 0)

  return (
    <Box rounded={'xl'} bg={'ninja.700'} maxW={'310px'} py={3} px={4}>
      <Flex mb={2.5} justifyContent={'space-between'} color={'ninja.200'} fontSize={'xs'} fontWeight={700}>
        <Text>{t('basket.contents')}</Text>
        <Text>{t('basket.value')}</Text>
      </Flex>
      {assets.map((asset, key) => (
        <Flex key={key} mb={1} justifyContent={'space-between'}>
          <Amount
            iconSize={'18px'}
            wrapperStyles={{
              fontSize: 'sm',
            }}
            value={asset.amount}
            denom={INJ_DENOM}
          />
          <Text fontSize={'sm'} fontWeight={600} color={'ninja.300'}>
            ~{Number(asset.value.toFixed(2)).toLocaleString()} USD
          </Text>
        </Flex>
      ))}
      {isHistorical ? (
        <Flex
          fontSize={'sm'}
          fontWeight={600}
          color={'ninja.100'}
          borderTop={'1px solid'}
          borderColor={'ninja.400'}
          pt={2}
          mt={2}
          justifyContent={'space-between'}
        >
          <Text>{t('common.sum')}</Text>
          <Text color={'ninja.300'}>~{Number(totalValue.toFixed(2)).toLocaleString()}$</Text>
        </Flex>
      ) : (
        <Flex
          fontSize={'sm'}
          fontWeight={600}
          color={'ninja.100'}
          borderTop={'1px solid'}
          borderColor={'ninja.400'}
          pt={2}
          mt={2}
          justifyContent={'space-between'}
        >
          <Text>{t('common.approximate')}</Text>
          <Text color={'ninja.300'}>~{Number((totalValue / nbzPrice).toFixed(2)).toLocaleString()} NBZ</Text>
        </Flex>
      )}
    </Box>
  )
}

type Bid = {
  id: any
  bet: any
  player: any
  created?: any
  txHash?: any
}

const BasketRoundBids = ({ bids, loading }: { bids?: Array<Bid>; loading: boolean }) => {
  const { t } = useTranslation()
  if (loading) {
    return (
      <SimpleGrid mb={10} columns={5} gap={4}>
        <Skeleton h={'146px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={'146px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={'146px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={'146px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={'146px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
      </SimpleGrid>
    )
  }

  if (!bids?.length) {
    return (
      <NoResultsFallback
        text={t('basket.no-bids')}
        styles={{
          h: '146px',
        }}
      />
    )
  }

  return (
    <SimpleGrid mb={10} columns={{ base: 1, md: 2, xl: 4, '2xl': 5 }} gap={4}>
      {[...bids].reverse().map((bid, index) => (
        <BasketRoundBidsElement bid={bid} key={index} isHighest={index === 0} />
      ))}
    </SimpleGrid>
  )
}

const BasketRoundBidsElement = ({ bid, isHighest = false }: { isHighest?: boolean; bid: Bid }) => {
  const { t } = useTranslation()
  return (
    <Box
      as={'a'}
      href={EXPLORERS.INJECTIVE_TRANSACTIONS + bid.txHash}
      target="_blank"
      p={0.5}
      bg={isHighest ? 'linear-gradient(-135deg, rgba(142,131,251,1) 0%, rgba(93,82,238,1) 100%)' : '#1c1c42'}
      rounded={'2xl'}
    >
      <Box
        p={4}
        bg={'ninja.600'}
        transition={'0.2s'}
        cursor={'pointer'}
        _hover={{
          bg: 'ninja.500',
        }}
        rounded={'2xl'}
        gap={4}
      >
        <Flex mb={3} alignItems={'start'} justifyContent={'space-between'}>
          <Box>
            <Text fontSize={'sm'} color={'ninja.200'} fontWeight={500} mb={1}>
              {t('basket.bid-placed-by')}
            </Text>
            <UserProfile address={bid.player} />
          </Box>
          <Text
            fontSize={'xs'}
            color={'ninja.100'}
            fontWeight={500}
            border={'1px solid'}
            borderColor={'ninja.200'}
            rounded={'md'}
            px={1.5}
            py={0.5}
            whiteSpace={'nowrap'}
          >
            {t('common.tx')} {getTrimmedValue(bid.txHash, 4, 4, TrimVariant.IN)}
          </Text>
        </Flex>
        <Flex alignItems={'center'} gap={1}>
          <Icon boxSize={6} as={Logo} />

          <Text fontSize={'xl'} fontWeight={600}>
            {new TokenMoney(bid.bet, NBZ_DENOM).humanLocaleString}{' '}
            <Text as={'span'} color={'#B6AEFF'}>
              NBZ
            </Text>
          </Text>
        </Flex>
        <Text color={'ninja.200'} fontWeight={500} fontSize={'xs'}>
          {new Date(bid.created).toLocaleString()}
        </Text>
      </Box>
    </Box>
  )
}

const UserProfile = ({ address }: { address: string }) => {
  const { data } = useGetUserProfileByAddress(address)
  const modal = useAppModal()

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    modal.openModalNew(AppModals.UserStats, { address })
  }

  return (
    <Flex onClick={handleClick} gap={2} alignItems={'center'} cursor={'pointer'}>
      <AvatarCircle offset={0} boxSize={6} src={data?.user?.profileAvatar} />
      {!!data?.user ? (
        <GradientText
          props={{
            fontSize: 'sm',
            fontWeight: 600,
          }}
          numberOfNfts={data?.nfts?.totalCount || 0}
        >
          {data.user?.profileUserName}
        </GradientText>
      ) : (
        address && (
          <Text fontSize={'sm'} fontWeight={600}>
            {getTrimmedValue(address, 6, 6, TrimVariant.IN)}
          </Text>
        )
      )}
    </Flex>
  )
}

type Auction = {
  id: any
  basket: any
  status: AuctionBasketStatus
  bet: any
  leader?: string | null
  created: any
  end: any
  auctionBasketBets: Array<Bid>
}

const BasketHistory = ({ rounds, id, loading }: { rounds?: Array<Auction>; id?: string; loading: boolean }) => {
  const { t } = useTranslation()
  if (loading) {
    return (
      <Grid gap={3}>
        <Skeleton h={16} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={16} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={16} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={16} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
        <Skeleton h={16} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
      </Grid>
    )
  }

  if (!rounds?.length) {
    return (
      <NoResultsFallback
        text={t('basket.no-history')}
        styles={{
          h: '149px',
        }}
      />
    )
  }

  return (
    <Flex flexDir={'column'} gap={3} overflow={'auto'}>
      {rounds.map((round, index) => (
        <BasketHistoryElelemnt isCurrent={!id ? index === 0 : round.id === id} key={round.id} round={round} />
      ))}
    </Flex>
  )
}

const BasketHistoryElelemnt = ({ round, isCurrent }: { round: Auction; isCurrent: boolean }) => {
  const { t } = useTranslation()
  const isActive = round.status === AuctionBasketStatus.Active

  const props = !isCurrent
    ? {
        as: Link,
        href: '/auction/' + round.id,
      }
    : {}

  return (
    <Flex
      {...props}
      p={5}
      rounded={'xl'}
      bg={!isCurrent ? 'ninja.600' : 'ninja.500'}
      transition={'0.2s'}
      cursor={isCurrent ? 'default' : 'pointer'}
      _hover={{
        bg: 'ninja.500',
      }}
      justifyContent={'space-between'}
      alignItems={'center'}
      minW={'628px'}
    >
      <Flex alignItems={'center'} gap={4} fontWeight={600}>
        {isActive && <Pulsar />}
        <Text color={'ninja.200'}>
          {t('basket.round-number', { id: round.id })}
        </Text>
        {!isActive && !!round.leader && <UserProfile address={round.leader!} />}
      </Flex>
      <Flex alignItems={'center'} gap={3}>
        <Flex alignItems={'center'} gap={1}>
          <Icon boxSize={5} as={Logo} />
          <Text whiteSpace={'nowrap'} as={'span'} fontWeight={700} fontSize={'sm'}>
            {new TokenMoney(round.bet, NBZ_DENOM).humanLocaleString}{' '}
            <Text ml={1} color={'ninja.300'} as="span">
              NBZ
            </Text>
          </Text>
        </Flex>
        <Text ml={1} fontSize={'sm'} fontWeight={600} color={'ninja.200'}>
          {new Date(round.end).toLocaleString()}
        </Text>
        <Icon w={6} h={6} as={Eye} />
      </Flex>
    </Flex>
  )
}

const BasketTitle = ({ totalBurned }: { totalBurned?: string }) => {
  const { t } = useTranslation()
  return (
    <Flex
      mb={{ base: 4, md: 6, xl: 10 }}
      flexDir={{ base: 'column', md: 'row' }}
      justifyContent={'space-between'}
      alignItems={{ base: 'start', md: 'center' }}
    >
      <Heading fontSize={{ base: '36px', md: '44px', xl: '64px' }}>{t('common.auction-basket')}</Heading>
      <AuctionBasketTotalBurn totalBurned={totalBurned} />
    </Flex>
  )
}

const AuctionBasketTotalBurn = ({ totalBurned }: { totalBurned?: string }) => {
  const { t } = useTranslation()
  if (!totalBurned) {
    return (
      <Skeleton
        startColor="ninja.400"
        endColor="ninja.500"
        w={'300px'}
        h={'60px'}
        display={'inline-block'}
        rounded={'lg'}
      />
    )
  }

  return (
    <Flex alignItems={{ base: 'start', md: 'end' }} flexDir={'column'}>
      <Text lineHeight={'16px'} color={'ninja.300'} mb={{ base: 1, md: 0 }} fontSize={'sm'} fontWeight={600}>
        {t('basket.total-burned')}
      </Text>
      <Flex alignItems={'center'} gap={2}>
        <Icon boxSize={{ base: 8, xl: 12 }} as={Logo} />
        <Text
          lineHeight={{ base: '32px', xl: '48px' }}
          fontSize={{ base: '32px', xl: '48px' }}
          fontWeight={600}
          color={'ninja.100'}
        >
          {new TokenMoney(totalBurned, NBZ_DENOM).humanLocaleString}{' '}
          <Text as={'span'} color={'ninja.200'}>
            NBZ
          </Text>
        </Text>
      </Flex>
    </Flex>
  )
}

const BasketForm = ({
  startDate,
  endDate,
  currentBid,
  roundId,
  status,
  isHistorical,
  refetch,
}: {
  startDate: string
  endDate: string
  currentBid: string
  roundId: string
  status: AuctionBasketStatus
  isHistorical: boolean
  refetch: () => void
}) => {
  const { balance } = useBalanceStore()
  const { account, sendTransaction, signIn } = useSession()
  const bid = formatAmount(currentBid, { decimals: 6 })
  const [amount, setAmount] = useState('')
  const [isFinished, setFinished] = useState(new Date().getTime() > new Date(endDate).getTime())
  const { onError } = useAppToast()
  const modal = useAppModal()

  useEffect(() => {
    setFinished(new Date().getTime() > new Date(endDate).getTime())
  }, [endDate])

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (input: string) => {
      if (!account) {
        signIn()
        return
      }

      const result = await sendTransaction(
        MsgExecuteContractCompat.fromJSON({
          sender: account?.injectiveAddress!,
          contractAddress: AUCTION_BASKET_CONTRACT_ADDRESS,
          funds: [
            {
              amount: toBigNumberInWei(input, NBZ_DENOM_BASE).toString(),
              denom: NBZ_DENOM,
            },
          ],
          msg: {
            join: {
              id: Number(roundId),
            },
          },
        }),
        { showError: false }
      )

      if (result?.code === 0) {
        refetch()
        setAmount('')
      }
    },
    onError(error: any) {
      const errorCode = error.originalMessage.split(':').at(-2).replaceAll(' ', '')
      const errorKey = mappedErrorKeys[errorCode as keyof typeof mappedErrorKeys]
      onError({ custom: { title: errorKey ? t(errorKey) : t('messages.something-went-wrong') } })
      refetch()
    },
  })

  const handleRaise = () => {
    if (parseFloat(bid) === 0) {
      setAmount('0.1')
    } else {
      setAmount((Math.ceil(parseFloat(bid) * 1.01 * 1000) / 1000).toFixed(3))
    }
  }

  const isValid = (() => {
    const nbzBalance = balance[NBZ_DENOM]
    if (!nbzBalance || nbzBalance.bank.humanNumber == 0) {
      return true
    }

    if (nbzBalance.bank.humanNumber < parseFloat(amount) || parseFloat(amount) <= 0 || amount === '') {
      return false
    }

    if (parseFloat(amount) < Math.ceil(parseFloat(bid) * 1.01 * 1000) / 1000) {
      return false
    }

    return true
  })()

  const buttonProps = isHistorical
    ? {
        as: Link,
        href: '/auction',
      }
    : {}

  return (
    <Flex flexDir={'column'} justifyContent={'space-between'} p={6} rounded={'2xl'} bg={'ninja.600'}>
      <Flex mb={6} justifyContent={'space-between'}>
        <Box>
          <Text fontSize={'xl'} lineHeight={1} fontWeight={700}>
            {t('basket.round-number', { id: roundId })}
          </Text>
          <Text
            onClick={() => modal.openModalNew(AppModals.GameRules, { game: GameRules.BASKET })}
            cursor={'pointer'}
            _hover={{
              textDecor: 'underline',
            }}
            color={'#B6AEFF'}
            fontSize={'sm'}
            fontWeight={600}
          >
            {t('basket.how-does-it-work')}
          </Text>
        </Box>
        <Box textAlign={'right'}>
          <Text fontSize={'xs'} color={'ninja.300'} fontWeight={600}>
            {isFinished ? t('basket.auction-ended-at') : t('basket.auction-ends-at')}{' '}
          </Text>
          <Text fontSize={'sm'} fontWeight={700}>
            {new Date(endDate).toLocaleString()}
          </Text>
        </Box>
      </Flex>
      <ProgressBar setFinished={() => setFinished(true)} refetch={refetch} startDate={startDate} endDate={endDate} />
      <Box>
        <Flex mb={2} justifyContent={'end'}>
          <UserBalance />
        </Flex>
        <Flex
          bg={'ninja.500'}
          border={'2px solid'}
          borderColor={isValid || amount === '' ? 'ninja.500' : 'brand.error'}
          rounded={'xl'}
          alignItems={'center'}
          mb={4}
          h={'60px'}
          overflow={'hidden'}
        >
          <Input
            onChange={(e) => {
              const value = e.target.value
              const isPositive = new RegExp(/^\d+(\.\d*)?$|^\.\d+$/).test(value) || value === ''
              const isFloat = value.includes('.') ? value.split('.').at(-1)!.length <= 3 : true

              if (isPositive && isFloat) {
                setAmount(value)
              }
            }}
            value={amount}
            isDisabled={isFinished}
            h={'full'}
            border={'none'}
            fontWeight={700}
            placeholder={(Math.ceil(parseFloat(bid) * 1.01 * 1000) / 1000).toFixed(3)}
            _placeholder={{
              color: 'ninja.200',
            }}
            _focusVisible={{
              border: 'none',
            }}
          />
          <Button
            onClick={handleRaise}
            isDisabled={isFinished}
            flexShrink={0}
            variant={'unstyled'}
            h={'auto'}
            bg={'ninja.600'}
            mr={4}
            py={2}
            px={3}
            rounded={'lg'}
            fontWeight={700}
            cursor={'pointer'}
            fontSize={'sm'}
            color={'ninja.200'}
            transition={'0.1s'}
            userSelect={'none'}
            _hover={{
              color: 'ninja.100',
              bg: 'ninja.700',
            }}
          >
            {t('basket.raise')}
          </Button>
        </Flex>
        {status === AuctionBasketStatus.Active ? (
          <Button
            isLoading={isPending}
            isDisabled={!isValid || isFinished}
            onClick={() => mutateAsync(amount)}
            h={'52px'}
            w={'full'}
            rounded={'xl'}
          >
            {t('basket.place-bid')}
          </Button>
        ) : (
          <>
            <Button isDisabled={!isHistorical} variant={'orange'} h={'52px'} w={'full'} rounded={'xl'} {...buttonProps}>
              {!isHistorical ? t('basket.auction-finished') : t('basket.go-to-active')}
            </Button>
          </>
        )}
      </Box>
    </Flex>
  )
}

const mappedErrorKeys = {
  InvalidBet: 'basket.error-bid-too-low',
  AuctionEnded: 'basket.error-auction-ended',
}

const UserBalance = () => {
  const { t } = useTranslation()
  const { balance, isLoading } = useBalanceStore()

  const nbzBalance = balance[NBZ_DENOM]
  return (
    <Flex gap={2} alignItems={'center'}>
      <Text fontSize={'sm'} fontWeight={700} color={'ninja.300'}>
        {t('common.available')}:
      </Text>
      {!isLoading ? (
        <Flex alignItems={'center'} gap={1}>
          <Icon boxSize={5} as={Logo} />
          <Text whiteSpace={'nowrap'} as={'span'} fontWeight={700} fontSize={'sm'}>
            {nbzBalance.bank.humanNumber || '0.00'}
            <Text ml={1} color={'ninja.300'} as="span">
              NBZ
            </Text>
          </Text>
        </Flex>
      ) : (
        <Skeleton h={'21px'} w={'70px'} startColor="ninja.400" endColor="ninja.500" rounded={'2xl'} />
      )}
    </Flex>
  )
}

const oneMinute = 60 * 1_000
const ProgressBar = ({
  startDate,
  endDate,
  refetch,
  setFinished,
}: {
  startDate: string
  endDate: string
  refetch: () => void
  setFinished: () => void
}) => {
  const { t } = useTranslation()
  const endTimestamp = useRef(new Date(endDate).getTime())
  const duration = new Date(endDate).getTime() - new Date(startDate).getTime()
  const [time, setTime] = useState(Math.max(0, new Date(endDate).getTime() - new Date().getTime()))

  useEffect(() => {
    endTimestamp.current = new Date(endDate).getTime()
    setTime(Math.max(0, new Date(endDate).getTime() - new Date().getTime()))
  }, [startDate, endDate])

  useEffect(() => {
    const interval = setInterval(() => {
      const timeLeft = endTimestamp.current - new Date().getTime()

      if (timeLeft <= 2_000) {
        refetch()
      }

      if (timeLeft <= 0) {
        setTime(0)
        setFinished()
        clearInterval(interval)
        return
      }

      setTime(timeLeft)
    }, 1_000)

    return () => {
      clearInterval(interval)
    }
  }, [startDate, endDate])

  const progress = time <= oneMinute ? time / oneMinute : time / duration

  return (
    <>
      <Box mb={6}>
        <Flex fontWeight={700} mb={2} justifyContent={'space-between'} alignItems={'center'}>
          <Text color={'ninja.200'}>{t('common.time-left')}</Text>
          <Text
            fontFamily={'monospace'}
            color={time <= oneMinute ? 'brand.yellow' : 'white'}
            fontSize={'sm'}
            lineHeight={0.9}
            as={'span'}
          >
            {getFormattedTime(time)}
          </Text>
        </Flex>
        <Box rounded={'full'} bg={'whiteAlpha.100'} h={'15px'}>
          <Box rounded={'full'} overflow={'hidden'} h={'full'} transition={'0.2s'} w={progress}>
            <Box
              boxShadow={`-1px 0px 8px 0px #FFFFFFB5 inset;0px 0px 24px -15px #FFFFFF inset;`}
              sx={{
                '@keyframes animate': {
                  '0%': { bgPos: '0 0' },
                  '100%': { bgPos: '-500% 0' },
                },
              }}
              animation={'animate 15s linear infinite'}
              w={'full'}
              h={'full'}
              bg={
                time <= oneMinute ? 'brand.yellow' : `linear-gradient(100deg, #FC5DFF 0%, #9C5EFF 50%, #FC5DFF 100%);`
              }
              bgSize={'500%'}
            />
          </Box>
        </Box>
      </Box>
    </>
  )
}

const getFormattedTime = (time: number) => {
  return `${Math.floor(time / (3_600_000 * 24)).toString()}:${Math.floor((time / 3_600_000) % 24)
    .toString()
    .padStart(2, '0')}:${(Math.floor(time / 60_000) % 60).toString().padStart(2, '0')}:${(Math.floor(time / 1_000) % 60)
    .toString()
    .padStart(2, '0')}`
}
